// OfferChooserPage — /offer/<key>: pick a plan for a time-boxed offer that
// spans several checkouts (thread mct_5a7bac4e-a3b). First use: the NA Summit
// BKOS booth ("any yearly plan + a free course + 1:1 onboarding"), where the
// after-claim redirect lands here. Each plan's button goes to our own
// /checkout/<slug>?offer=<key>, which states the offer again and embeds the
// Spiffy checkout. Offer copy lives in worker/src/checkout-offers.ts; prices
// and contents come from the same buyer-intent configs the checkout reads.

import { useEffect, useState, type CSSProperties } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import BrokenPage from './components/BrokenPage';
import { PolicyLinks } from './components/PolicyLinks';
import { SUPPORT_EMAIL, BRAND_NAME, BRAND_HOME_URL, BRAND_LOGO_URL } from './branding';
import { fetchWithRetry } from './lib/fetchRetry';
import { useBrandGtm } from './lib/useBrandGtm';
import { OfferBanner, type CheckoutOfferPublic } from './CheckoutPage';

const API = import.meta.env.VITE_API_BASE_URL as string;

interface ChooserPlan {
  slug: string;
  intent_label: string;
  pricing: Record<string, { amount: number }> | null;
  price_display: string | null;
  price_suffix: string | null;
  included: string[] | null;
}

interface ChooserPayload {
  offer: CheckoutOfferPublic;
  chooser: { heading: string; subheading: string } | null;
  visitorCurrency: string | null;
  plans: ChooserPlan[];
  logoUrl: string | null;
  logoLinkUrl: string | null;
  brandName: string | null;
}

function priceFor(plan: ChooserPlan, currency: string | null): string | null {
  const entry = currency ? plan.pricing?.[currency] : undefined;
  if (entry) return `$${entry.amount.toLocaleString('en-US')} ${currency}${plan.price_suffix ?? ''}`;
  return plan.price_display;
}

/** The checkout link for a plan: the offer key plus every param the visitor
 *  arrived with (UTMs, click ids, ref), so attribution survives the hop. */
export function checkoutHref(slug: string, offerKey: string, arrived: URLSearchParams): string {
  const q = new URLSearchParams();
  for (const [k, v] of arrived) if (v && k !== 'offer') q.set(k, v);
  q.set('offer', offerKey);
  return `/checkout/${encodeURIComponent(slug)}?${q}`;
}

export function OfferChooserPage() {
  const { key = '' } = useParams<{ key: string }>();
  const [params] = useSearchParams();
  useBrandGtm();
  const [data, setData] = useState<ChooserPayload | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchWithRetry(`${API}/api/checkout-offers/${encodeURIComponent(key)}`, { headers: { Accept: 'application/json' } });
        if (res.status === 404) { if (!cancelled) setError('This offer has ended.'); return; }
        if (!res.ok) throw new Error(`offer ${res.status}`);
        const body = (await res.json()) as ChooserPayload;
        if (!cancelled) setData(body);
      } catch (err) {
        if (!cancelled) setError('We couldn\'t load this page. Please try again in a moment.');
        // eslint-disable-next-line no-console
        console.error('[offer] load failed', err);
      }
    })();
    return () => { cancelled = true; };
  }, [key]);

  if (error) return <BrokenPage variant="error" message={error} />;
  if (!data) {
    return (
      <main style={page}>
        <p style={{ color: '#64748B', fontSize: 15, textAlign: 'center', paddingTop: 80 }}>Loading…</p>
      </main>
    );
  }

  const currency = data.visitorCurrency;
  const bundles = data.plans.filter((p) => !/software only/i.test(p.intent_label));
  const software = data.plans.filter((p) => /software only/i.test(p.intent_label));

  const card = (plan: ChooserPlan) => (
    <div key={plan.slug} style={planCard} data-testid={`offer-plan-${plan.slug}`}>
      <h3 style={planName}>{plan.intent_label.replace(/\s*\((annual|yearly)\)\s*$/i, '')}</h3>
      {priceFor(plan, currency) && <div style={planPrice}>{priceFor(plan, currency)}</div>}
      {plan.included && (
        <ul style={planList}>
          {plan.included.map((item, i) => (
            <li key={i} style={planItem}><span style={check} aria-hidden="true">✓</span><span>{item}</span></li>
          ))}
        </ul>
      )}
      <a href={checkoutHref(plan.slug, data.offer.key, params)} style={planButton}>Choose this plan</a>
    </div>
  );

  return (
    <main style={page}>
      <div style={topBar}>
        <a href={data.logoLinkUrl ?? BRAND_HOME_URL} style={{ display: 'inline-flex' }} aria-label={data.brandName ?? BRAND_NAME}>
          <img src={data.logoUrl ?? BRAND_LOGO_URL} alt={data.brandName ?? BRAND_NAME} style={{ height: 40, width: 'auto', display: 'block' }} />
        </a>
      </div>
      <div style={shell}>
        <h1 style={heading}>{data.chooser?.heading ?? 'Choose your plan'}</h1>
        {data.chooser?.subheading && <p style={subheading}>{data.chooser.subheading}</p>}
        <OfferBanner offer={data.offer} />
        {bundles.length > 0 && (
          <>
            {software.length > 0 && <h2 style={groupHeading}>With your TSB membership included</h2>}
            <div style={grid}>{bundles.map(card)}</div>
          </>
        )}
        {software.length > 0 && (
          <>
            <h2 style={groupHeading}>Software only</h2>
            <div style={grid}>{software.map(card)}</div>
          </>
        )}
      </div>
      <footer style={foot}>
        Need help? <a href={`mailto:${SUPPORT_EMAIL}`} style={{ color: '#64748B' }}>{SUPPORT_EMAIL}</a>
        <div style={{ marginTop: 8 }}><PolicyLinks /></div>
      </footer>
    </main>
  );
}

const page: CSSProperties = {
  minHeight: '100vh',
  background: '#F8FAFC',
  padding: '0 0 48px',
  fontFamily: "'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
};
const topBar: CSSProperties = { background: '#ffffff', borderBottom: '1px solid #E2E8F0', padding: '16px 24px', marginBottom: 24 };
const shell: CSSProperties = { maxWidth: 1100, margin: '0 auto', padding: '0 16px' };
const heading: CSSProperties = { fontSize: 30, fontWeight: 800, color: '#0F172A', textAlign: 'center', margin: '8px 0 8px' };
const subheading: CSSProperties = { fontSize: 16, color: '#475569', textAlign: 'center', maxWidth: 640, margin: '0 auto 20px', lineHeight: 1.5 };
const groupHeading: CSSProperties = { fontSize: 18, fontWeight: 700, color: '#0F172A', margin: '28px 0 12px' };
const grid: CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 16 };
const planCard: CSSProperties = {
  background: '#ffffff', borderRadius: 14, padding: '22px 20px', display: 'flex', flexDirection: 'column',
  boxShadow: '0 10px 28px -14px rgba(15, 23, 42, 0.25)', border: '1px solid #E2E8F0',
};
const planName: CSSProperties = { fontSize: 18, fontWeight: 700, color: '#0F172A', margin: '0 0 6px' };
const planPrice: CSSProperties = { fontSize: 15, fontWeight: 600, color: '#334155', marginBottom: 12 };
const planList: CSSProperties = { listStyle: 'none', padding: 0, margin: '0 0 18px', flex: 1 };
const planItem: CSSProperties = { display: 'flex', gap: 8, fontSize: 14, color: '#334155', lineHeight: 1.45, marginBottom: 6 };
const check: CSSProperties = { color: '#059669', fontWeight: 700 };
const planButton: CSSProperties = {
  display: 'block', textAlign: 'center', background: '#0F172A', color: '#ffffff', borderRadius: 10,
  padding: '12px 16px', fontWeight: 700, fontSize: 15, textDecoration: 'none',
};
const foot: CSSProperties = { marginTop: 36, textAlign: 'center', fontSize: 12.5, color: '#94A3B8' };
